/**
 * La evidencia en pantalla.
 *
 * Regla del módulo: **toda alerta muestra la evidencia cruda que la
 * disparó**, también en el listado. Un investigador que tiene que abrir cada
 * renglón para saber por qué saltó algo deja de usar la bandeja.
 *
 * Los nombres de los campos se muestran **tal como los escribe el motor de
 * anomalías**. No se traducen a propósito: son los mismos que devuelve la API
 * y los mismos que quedan en el log del worker, y una investigación que
 * compara "distancia" en la pantalla con `distance_km` en el log es una
 * investigación que se equivoca de campo.
 *
 * Lo que la evidencia NUNCA lleva —ni IP, ni huella de dispositivo, ni
 * coordenadas finas— lo garantiza `services/shared/models.py` aguas arriba
 * (`PII_FIELD_NAMES`). Lo que sí puede traer son ids internos de unidad, y
 * eso se esconde acá: ver `INTERNAL_ID_KEYS` en `evidence.ts`.
 */
import type { ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import {
  evidenceEntries,
  evidenceWhy,
  highlights,
  readTwoScans,
  type EvidenceEntry,
  type EvidenceValue,
  type GeoPoint,
  type TwoScans,
} from './evidence';

/** Un número entero se muestra entero; uno con coma, con un decimal. */
function ValueView({ value }: { value: EvidenceValue }): ReactNode {
  const { t, fmt } = useI18n();

  switch (value.kind) {
    case 'text':
      return <span className="text-ink">{value.text}</span>;
    case 'number':
      return (
        <span className="font-mono text-ink">
          {fmt.number(value.value, Number.isInteger(value.value) ? 0 : 1)}
        </span>
      );
    case 'bool':
      return (
        <span className="text-ink">{value.value ? t('alerts.yes') : t('alerts.no')}</span>
      );
    case 'timestamp':
      return <span className="text-ink">{fmt.dateTime(value.iso)}</span>;
    case 'geo':
      return <span className="text-ink">{describePoint(value.point, t('alerts.evidence.unknownPlace'))}</span>;
    case 'list':
      return (
        <span className="text-ink">
          {value.items.length === 0 ? '—' : null}
          {value.items.map((item, index) => (
            <span key={index}>
              {index > 0 ? ' · ' : ''}
              <ValueView value={item} />
            </span>
          ))}
        </span>
      );
    case 'nested':
      return (
        <span className="text-ink">
          {value.entries.map((entry, index) => (
            <span key={entry.key}>
              {index > 0 ? ' · ' : ''}
              <span className="font-mono text-xs text-ink-subtle">{entry.key}</span>{' '}
              <ValueView value={entry.value} />
            </span>
          ))}
        </span>
      );
    case 'hidden':
      // El campo existe y se dice que existe: ocultarlo sin avisar haría
      // pensar que la evidencia está incompleta.
      return <span className="text-ink-subtle italic">{t('alerts.evidence.hidden')}</span>;
    case 'empty':
      return <span className="text-ink-subtle">—</span>;
  }
}

function describePoint(point: GeoPoint, fallback: string): string {
  const place = point.city ?? fallback;
  return point.country === null ? place : `${place} (${point.country})`;
}

/** Los dos o tres campos que deciden el triage, para el renglón de la lista. */
export function EvidenceChips({
  alertType,
  evidence,
}: {
  alertType: string;
  evidence: Record<string, unknown>;
}): ReactNode {
  const facts = highlights(alertType, evidence);
  if (facts.length === 0) return null;

  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      {facts.map((fact) => (
        <li key={fact.key} className="flex items-center gap-1">
          <span className="font-mono text-ink-subtle">{fact.key}</span>
          <ValueView value={fact.value} />
        </li>
      ))}
    </ul>
  );
}

/**
 * Los dos escaneos que no pueden ser la misma etiqueta, ubicados uno respecto
 * del otro.
 *
 * No usa MapLibre a propósito: el mapa de calor es el issue #43 y trae su
 * propio estilo, sus tiles y su capa de deck.gl. Meter esa dependencia acá
 * cargaría el mapa entero en la ruta de la bandeja —que se abre cien veces
 * por turno— para dibujar dos puntos, y ataría este módulo a decisiones que
 * todavía no se tomaron. Cuando #43 exista, este componente se reemplaza por
 * un `<MiniMap>` sin tocar nada más.
 *
 * Las coordenadas vienen redondeadas a un decimal desde el worker: es
 * geolocalización aproximada a nivel ciudad, que es lo máximo que la regla 5
 * permite mostrarle al tenant.
 */
function ScanGeoPlot({ scans }: { scans: TwoScans }): ReactNode {
  const { t } = useI18n();
  const { from, to } = scans;
  if (from.lat === null || from.lon === null || to.lat === null || to.lon === null) {
    return null;
  }

  const midLon = (from.lon + to.lon) / 2;
  const midLat = (from.lat + to.lat) / 2;
  const lonSpan = Math.abs(from.lon - to.lon);
  const latSpan = Math.abs(from.lat - to.lat);
  // Escala uniforme: deformar el eje para "llenar" la caja haría que dos
  // escaneos separados por 200 km parezcan estar en la otra punta del mundo.
  const width = Math.max(lonSpan * 1.8, latSpan * 3.6, 8);
  const height = width / 2;
  const x0 = midLon - width / 2;
  const y0 = -midLat - height / 2;
  const step = width > 120 ? 30 : width > 40 ? 10 : width > 12 ? 5 : 1;

  const project = (lon: number, lat: number): [number, number] => [lon, -lat];
  const [ax, ay] = project(from.lon, from.lat);
  const [bx, by] = project(to.lon, to.lat);
  const radius = width / 90;

  const lines: ReactNode[] = [];
  for (let lon = Math.ceil(x0 / step) * step; lon < x0 + width; lon += step) {
    lines.push(
      <line
        key={`v${lon}`}
        x1={lon}
        y1={y0}
        x2={lon}
        y2={y0 + height}
        stroke="currentColor"
        strokeWidth={width / 900}
      />,
    );
  }
  for (let y = Math.ceil(y0 / step) * step; y < y0 + height; y += step) {
    lines.push(
      <line
        key={`h${y}`}
        x1={x0}
        y1={y}
        x2={x0 + width}
        y2={y}
        stroke="currentColor"
        strokeWidth={width / 900}
      />,
    );
  }

  return (
    <figure className="m-0">
      <svg
        viewBox={`${x0} ${y0} ${width} ${height}`}
        className="w-full rounded border border-border bg-surface-sunken"
        role="img"
        aria-label={t('alerts.travel.plotLabel', {
          from: describePoint(from, t('alerts.evidence.unknownPlace')),
          to: describePoint(to, t('alerts.evidence.unknownPlace')),
        })}
      >
        <g className="text-border">{lines}</g>
        <line
          x1={ax}
          y1={ay}
          x2={bx}
          y2={by}
          className="text-critical"
          stroke="currentColor"
          strokeWidth={width / 400}
          strokeDasharray={`${width / 120} ${width / 120}`}
        />
        <circle cx={ax} cy={ay} r={radius} className="text-ink-muted" fill="currentColor" />
        <circle cx={bx} cy={by} r={radius} className="text-critical" fill="currentColor" />
      </svg>
      <figcaption className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
        <span className="flex items-center gap-1">
          <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-ink-muted" />
          {describePoint(from, t('alerts.evidence.unknownPlace'))}
        </span>
        <span className="flex items-center gap-1">
          <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-critical" />
          {describePoint(to, t('alerts.evidence.unknownPlace'))}
        </span>
        <span>{t('alerts.travel.approximate')}</span>
      </figcaption>
    </figure>
  );
}

/** El par de escaneos con su distancia, su tiempo y su velocidad. */
function TwoScansPanel({ scans }: { scans: TwoScans }): ReactNode {
  const { t, fmt } = useI18n();

  return (
    <div className="space-y-3 rounded border border-critical/30 bg-critical-soft/40 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <ScanCard title={t('alerts.travel.first')} point={scans.from} />
        <ScanCard title={t('alerts.travel.second')} point={scans.to} />
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-2">
        {scans.distanceKm !== null && (
          <Metric label={t('alerts.travel.distance')} value={t('alerts.travel.km', { value: fmt.number(scans.distanceKm, 1) })} />
        )}
        {scans.elapsedMinutes !== null && (
          <Metric label={t('alerts.travel.elapsed')} value={t('alerts.travel.minutes', { value: fmt.number(scans.elapsedMinutes, 1) })} />
        )}
        {scans.speedKmh !== null && (
          <Metric
            label={t('alerts.travel.speed')}
            value={t('alerts.travel.kmh', { value: fmt.number(scans.speedKmh, 1) })}
            critical
          />
        )}
        {scans.maxSpeedKmh !== null && (
          <Metric label={t('alerts.travel.limit')} value={t('alerts.travel.kmh', { value: fmt.number(scans.maxSpeedKmh, 0) })} />
        )}
      </dl>

      <ScanGeoPlot scans={scans} />
    </div>
  );
}

function ScanCard({ title, point }: { title: string; point: GeoPoint }): ReactNode {
  const { t, fmt } = useI18n();
  return (
    <div className="rounded border border-border bg-surface p-2">
      <p className="text-xs tracking-wide text-ink-subtle uppercase">{title}</p>
      <p className="text-sm font-medium text-ink">
        {describePoint(point, t('alerts.evidence.unknownPlace'))}
      </p>
      <p className="text-xs text-ink-muted">{point.at === null ? '—' : fmt.dateTime(point.at)}</p>
    </div>
  );
}

function Metric({
  label,
  value,
  critical = false,
}: {
  label: string;
  value: string;
  critical?: boolean;
}): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd className={`font-mono text-sm ${critical ? 'font-semibold text-critical' : 'text-ink'}`}>
        {value}
      </dd>
    </div>
  );
}

function FieldRow({ entry }: { entry: EvidenceEntry }): ReactNode {
  return (
    <div className="flex flex-wrap items-baseline gap-2 border-b border-border py-1.5 last:border-b-0">
      <dt className="w-56 shrink-0 font-mono text-xs text-ink-subtle">{entry.key}</dt>
      <dd className="text-sm">
        <ValueView value={entry.value} />
      </dd>
    </div>
  );
}

/** La evidencia completa: el porqué, los dos escaneos si los hay, y el resto. */
export function EvidencePanel({
  alertType,
  evidence,
}: {
  alertType: string;
  evidence: Record<string, unknown>;
}): ReactNode {
  const { t } = useI18n();
  const why = evidenceWhy(evidence);
  const scans = readTwoScans(evidence);
  const entries = evidenceEntries(evidence);

  return (
    <section className="space-y-3" aria-label={t('alerts.evidence.title')}>
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-ink">{t('alerts.evidence.title')}</h3>
        <Badge tone="neutral">{alertType}</Badge>
      </div>

      {why !== null && <p className="max-w-prose text-sm text-ink-muted">{why}</p>}

      {scans !== null && <TwoScansPanel scans={scans} />}

      {entries.length === 0 ? (
        <p className="text-sm text-ink-subtle">{t('alerts.evidence.empty')}</p>
      ) : (
        <dl className="rounded border border-border px-3 py-1">
          {entries.map((entry) => (
            <FieldRow key={entry.key} entry={entry} />
          ))}
        </dl>
      )}
    </section>
  );
}
